import type { Vec2, Vec3 } from '../geo/types';
import type { Pose, InputFrame } from './types';
import type { CollisionWorld } from '../nav/types';
import { PLAYER } from '../core/config';
import { clamp } from '../core/math';
import { layoutToWorld, worldToLayout } from '../core/coords';

export interface WalkState {
  readonly pose: Pose;
  readonly velocity: Vec3;
}

export function createWalkState(spawn: Vec2, groundHeightAt: (p: Vec2) => number): WalkState {
  const groundY = groundHeightAt(spawn);
  const position = layoutToWorld(spawn, groundY + PLAYER.eyeHeight);

  return {
    pose: {
      position,
      yaw: 0,
      pitch: 0,
      roll: 0,
      fovYDeg: 60,
    },
    velocity: [0, 0, 0],
  };
}

export function updateWalk(
  state: WalkState,
  input: InputFrame,
  dt: number,
  collision: CollisionWorld,
  groundHeightAt: (p: Vec2) => number
): WalkState {
  const { pose } = state;

  const newYaw =
    pose.yaw -
    input.lookDx * PLAYER.lookRadPerPx -
    (input.turn ?? 0) * PLAYER.turnRadPerSecond * dt;
  const newPitch = clamp(
    pose.pitch - input.lookDy * PLAYER.lookRadPerPx,
    -PLAYER.pitchLimit,
    PLAYER.pitchLimit
  );

  const targetSpeed =
    (input.boost ? PLAYER.runSpeed : PLAYER.walkSpeed) *
    (input.forward < 0 ? PLAYER.backwardSpeedFactor : 1);
  const length = Math.max(1, Math.hypot(input.forward, input.right));

  const cosYaw = Math.cos(newYaw);
  const sinYaw = Math.sin(newYaw);

  const forwardX = -sinYaw;
  const forwardZ = -cosYaw;
  const rightX = cosYaw;
  const rightZ = -sinYaw;

  const targetVelX = ((input.forward * forwardX + input.right * rightX) * targetSpeed) / length;
  const targetVelZ = ((input.forward * forwardZ + input.right * rightZ) * targetSpeed) / length;

  const moving = input.forward !== 0 || input.right !== 0;
  const follow = 1 - Math.exp(-Math.max(0, dt) / 0.075);
  const newVelX = moving ? state.velocity[0] + (targetVelX - state.velocity[0]) * follow : 0;
  const newVelZ = moving ? state.velocity[2] + (targetVelZ - state.velocity[2]) * follow : 0;
  const deltaX = newVelX * dt;
  const deltaZ = newVelZ * dt;

  const currentLayout = worldToLayout(pose.position);
  const deltaLayout: Vec2 = [deltaX, -deltaZ];

  const newLayout = collision.move(currentLayout, deltaLayout);
  const groundY = groundHeightAt(newLayout);
  const newPosition = layoutToWorld(newLayout, groundY + PLAYER.eyeHeight);

  return {
    pose: {
      position: newPosition,
      yaw: newYaw,
      pitch: newPitch,
      roll: 0,
      fovYDeg: pose.fovYDeg,
    },
    velocity:
      dt > 0
        ? [(newPosition[0] - pose.position[0]) / dt, 0, (newPosition[2] - pose.position[2]) / dt]
        : [0, 0, 0],
  };
}
